import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router, Link, Route, Routes, useSearchParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import App from "./App";
import CxChatPage from "./CxChatPage";
import { auth, signInWithGoogle, signOut, getEnvironmentConfig } from "./firebase";
import { defaultDashboardSummary, fetchDashboardSummary } from "./dashboardData";
import { loadRecentUserVideos } from "./videoLibraryData";
import LandingPage from "./LandingPage";

const API_URLS = {
  dev: {
    listUserVideos: "https://us-central1-tennis-uploader-dev.cloudfunctions.net/listUserVideos",
    getVideoSummary: "https://us-central1-tennis-uploader-dev.cloudfunctions.net/getVideoSummary",
    getServePoseSequence: "https://us-central1-tennis-uploader-dev.cloudfunctions.net/getServePoseSequence",
  },
  stg: {
    listUserVideos: "https://us-central1-tennis-uploader-stg.cloudfunctions.net/listUserVideos",
    getVideoSummary: "https://us-central1-tennis-uploader-stg.cloudfunctions.net/getVideoSummary",
    getServePoseSequence: "https://us-central1-tennis-uploader-stg.cloudfunctions.net/getServePoseSequence",
  },
  prod: {
    listUserVideos: "https://us-central1-tennis-uploader.cloudfunctions.net/listUserVideos",
    getVideoSummary: "https://us-central1-tennis-uploader.cloudfunctions.net/getVideoSummary",
    getServePoseSequence: "https://us-central1-tennis-uploader.cloudfunctions.net/getServePoseSequence",
  },
};

const PROCESSING_MESSAGES = [
  {
    title: "Your serve is in the lab.",
    detail: "Every stronger serve is built one intentional rep at a time.",
  },
  {
    title: "We are tracing the movement.",
    detail: "Small adjustments become clearer when you can see the evidence.",
  },
  {
    title: "Your next cue is taking shape.",
    detail: "Good feedback is specific, visual, and built around your motion.",
  },
  {
    title: "The evidence is loading.",
    detail: "You are building a serve, not chasing a score.",
  },
  {
    title: "Stay in the zone.",
    detail: "Your report will point to one clear next move.",
  },
];

function getApiUrls() {
  const projectId = getEnvironmentConfig().projectId || "tennis-uploader";
  if (projectId.includes("stg")) return API_URLS.stg;
  if (projectId.includes("dev")) return API_URLS.dev;
  return API_URLS.prod;
}

async function fetchServeArchive(token, { before = "", date = "", serveNumber = 0 } = {}) {
  const params = new URLSearchParams({ view: "archive", page_size: "50" });
  if (before) params.set("before", before);
  if (date) params.set("date", date);
  if (serveNumber > 0) params.set("serve_number", String(serveNumber));
  const response = await fetch(`${getApiUrls().listUserVideos}?${params}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`Unable to load serve archive (${response.status})`);
  }

  return response.json();
}

async function fetchVideoSummary(token, videoId) {
  const response = await fetch(`${getApiUrls().getVideoSummary}?video_id=${encodeURIComponent(videoId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Unable to load this analysis (${response.status})`);
  }

  return response.json();
}

async function fetchServePoseSequence(token, videoId, startFrame, endFrame) {
  const params = new URLSearchParams({
    video_id: videoId,
    start_frame: String(startFrame),
    end_frame: String(endFrame),
  });
  const response = await fetch(`${getApiUrls().getServePoseSequence}?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Unable to load skeleton motion (${response.status})`);
  }

  const result = await response.json();
  return Array.isArray(result.frames) ? result.frames : [];
}

function LibraryPage() {
  const [user, setUser] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const [pendingVideo, setPendingVideo] = useState(
    () => searchParams.get("pending_video") || localStorage.getItem("pendingVideo")
  );
  const [comparisonReady, setComparisonReady] = useState(null);
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let active = true;

    async function loadVideos() {
      try {
        const list = await loadRecentUserVideos(user);
        if (active) {
          setVideos(Array.isArray(list) ? list : []);
          setError("");
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message || "Could not load your videos.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadVideos();
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    if (!user || !pendingVideo) return;

    let cancelled = false;
    let timeoutId;

    async function pollForReady() {
      try {
        const refreshed = await loadRecentUserVideos(user, { force: true });
        const completedVideo = refreshed.find((video) => {
          const videoId = String(video?.name || "").split("/").pop().replace(/^annotated_/, "");
          return videoId === pendingVideo && ["DONE", "POSE_DONE"].includes(video.status);
        });
        if (!cancelled && completedVideo) {
          localStorage.removeItem("pendingVideo");
          setPendingVideo(null);
          window.history.replaceState(null, "", "/library");
          const baselineVideoId = localStorage.getItem("comparisonBaselineVideo");
          if (baselineVideoId && baselineVideoId !== pendingVideo) {
            setComparisonReady({ videoId: pendingVideo, baselineVideoId });
            localStorage.removeItem("comparisonBaselineVideo");
          }
          setVideos(refreshed);
          return;
        }
      } catch (loadError) {
        console.warn("Pending serve status check failed:", loadError);
      }

      if (!cancelled) {
        timeoutId = setTimeout(pollForReady, 20000);
      }
    }

    pollForReady();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [user, pendingVideo]);

  useEffect(() => {
    if (!pendingVideo) {
      setProcessingMessageIndex(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setProcessingMessageIndex((index) => (index + 1) % PROCESSING_MESSAGES.length);
    }, 5500);
    return () => window.clearInterval(intervalId);
  }, [pendingVideo]);

  const visibleVideos = useMemo(() => {
    const unique = new Map();
    videos.forEach((video) => {
      if (!video || !video.name) return;
      const cleanName = video.name.split("/").pop().replace(/^annotated_/, "");
      if (!unique.has(cleanName)) unique.set(cleanName, video);
    });
    return Array.from(unique.values()).sort((a, b) => (b.timeCreated || "").localeCompare(a.timeCreated || ""));
  }, [videos]);

  if (!user) {
    return (
      <main className="app-shell app-page">
        <div className="page-shell auth-page">
          <p className="eyebrow">SERVE LIBRARY</p>
          <h1>Sign in to view your videos</h1>
          <p className="lead">Your serve reps and coaching evidence appear here once you are signed in.</p>
          <button className="button button-primary" onClick={signInWithGoogle}>Continue with Google</button>
        </div>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <Link className="wordmark" to="/">
          <span className="wordmark-icon">🎾</span>
          <span>Evolve <span className="wordmark-accent">Sports</span></span>
        </Link>
        <div className="account">
          <Link className="button button-quiet" to="/archive">Serve archive</Link>
          <button className="button button-quiet" onClick={() => signOut(auth)}>Sign out</button>
        </div>
      </header>

      <div className="dashboard">
        <section className="hero">
          <div>
            <p className="eyebrow">SERVE LIBRARY</p>
            <h1>Every rep tells the story.</h1>
            <p className="hero-copy">Review your serve reps, track what is processing, and return to the coaching evidence that moves you forward.</p>
          </div>
        </section>

        {pendingVideo ? (
          <div className="status-banner status-banner-processing">
            <span>⏳</span>
            <div>
              <strong>{PROCESSING_MESSAGES[processingMessageIndex].title}</strong>
              <small>{PROCESSING_MESSAGES[processingMessageIndex].detail}</small>
            </div>
          </div>
        ) : null}
        {comparisonReady ? (
          <div className="status-banner status-banner-ready">
            <span>✓</span>
            <div>
              <strong>Your next rep is ready to compare</strong>
              <small>Review matching moments with the serve you used as your baseline.</small>
            </div>
            <Link
              className="button button-primary"
              to={`/compare?video_id=${encodeURIComponent(comparisonReady.videoId)}&baseline_video_id=${encodeURIComponent(comparisonReady.baselineVideoId)}`}
            >
              Open comparison
            </Link>
          </div>
        ) : null}

        {loading ? (
          <div className="empty-state">Loading sessions…</div>
        ) : error ? (
          <div className="empty-state">{error}</div>
        ) : visibleVideos.length === 0 ? (
          <div className="empty-state">
            <div>
              <h2>No videos yet</h2>
              <p>Your next serve rep will appear here when it is ready.</p>
              <Link className="button button-primary" to="/">Build your first rep</Link>
            </div>
          </div>
        ) : (
          <div className="session-grid library-grid">
            {visibleVideos.map((video) => {
              const name = video.name.split("/").pop().replace(/^annotated_/, "");
              const session = formatSessionTitle(name, video.serveNumber);
              const summary = Array.isArray(video.summary) ? video.summary : [];
              const isReady = summary.length > 0 || ["DONE", "POSE_DONE"].includes(video.status);
              const videoUrl = video.url || "";

              return (
                <article key={`${name}-${video.status}`} className="session-card library-card">
                  <div className="session-topline">
                    <span className="session-tag">{isReady ? "Ready" : "Processing"}</span>
                    <strong>{summary.length || 0} insights</strong>
                  </div>

                  {videoUrl ? (
                    <video className="library-video" src={videoUrl} controls preload="metadata" />
                  ) : (
                    <div className="library-video placeholder">Video preview unavailable</div>
                  )}

                  <h3>{session.title}</h3>
                  <p>{video.timeCreated ? new Date(video.timeCreated).toLocaleString() : session.detail}</p>
                  <div className="session-actions">
                    <Link className="button button-primary small-button" to={`/feedback?video_id=${encodeURIComponent(name)}`}>
                      Open analysis
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}

function formatArchiveTimestamp(recordedAt) {
  if (!recordedAt) return "Recorded time unavailable";
  return new Date(recordedAt).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function ServeArchivePage() {
  const [user, setUser] = useState(null);
  const [serves, setServes] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [search, setSearch] = useState("");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const parsedSearch = search.trim().replace(/^#/, "");
  const serveNumber = /^\d+$/.test(parsedSearch) ? Number(parsedSearch) : 0;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    async function loadArchive() {
      try {
        const result = await fetchServeArchive(await user.getIdToken(true), { date, serveNumber });
        if (!active) return;
        setServes(Array.isArray(result.videos) ? result.videos : []);
        setNextCursor(result.nextCursor || null);
        setError("");
      } catch (loadError) {
        if (active) setError(loadError.message || "Could not load your serve archive.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadArchive();
    return () => {
      active = false;
    };
  }, [user, date, serveNumber]);

  async function loadMore() {
    if (!user || !nextCursor) return;
    setLoadingMore(true);
    try {
      const result = await fetchServeArchive(await user.getIdToken(true), {
        before: nextCursor,
        date,
        serveNumber,
      });
      const moreServes = Array.isArray(result.videos) ? result.videos : [];
      setServes((current) => {
        const known = new Set(current.map((serve) => serve.videoId));
        return [...current, ...moreServes.filter((serve) => !known.has(serve.videoId))];
      });
      setNextCursor(result.nextCursor || null);
    } catch (loadError) {
      setError(loadError.message || "Could not load more serves.");
    } finally {
      setLoadingMore(false);
    }
  }

  const visibleServes = serves.filter((serve) => {
    if (!search || serveNumber) return true;
    return formatArchiveTimestamp(serve.recordedAt).toLowerCase().includes(search.trim().toLowerCase());
  });

  if (!user) {
    return (
      <main className="app-shell app-page">
        <div className="page-shell auth-page">
          <p className="eyebrow">SERVE ARCHIVE</p>
          <h1>Sign in to find every serve.</h1>
          <p className="lead">Search your recorded serve history and open any completed analysis.</p>
          <button className="button button-primary" onClick={signInWithGoogle}>Continue with Google</button>
        </div>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <Link className="wordmark" to="/"><span className="wordmark-icon">🎾</span><span>Evolve <span className="wordmark-accent">Sports</span></span></Link>
        <div className="account"><Link className="button button-quiet" to="/library">Back to library</Link></div>
      </header>
      <div className="dashboard archive-page">
        <section className="archive-hero">
          <div>
            <p className="eyebrow">SERVE ARCHIVE</p>
            <h1>Find any serve, any day.</h1>
            <p className="hero-copy">Every completed analysis in chronological order. Search a serve number or choose a recording date.</p>
          </div>
          <span className="step-badge subtle">{serves.length} loaded</span>
        </section>
        <section className="archive-controls" aria-label="Filter serve archive">
          <label>
            <span>Find serve #</span>
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="e.g. 50" inputMode="numeric" />
          </label>
          <label>
            <span>Recorded on</span>
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </label>
          {date || search ? <button className="button secondary-link" type="button" onClick={() => { setSearch(""); setDate(""); }}>Clear filters</button> : null}
        </section>
        {loading ? <div className="empty-state">Loading your serve archive…</div> : error ? (
          <div className="empty-state">{error}</div>
        ) : visibleServes.length === 0 ? (
          <div className="empty-state">No completed serves match that filter.</div>
        ) : (
          <>
            <section className="archive-list">
              {visibleServes.map((serve) => (
                <article className="archive-row" key={serve.videoId}>
                  <div className="archive-serve-number">#{serve.serveNumber}</div>
                  <div>
                    <strong>Serve Rep #{serve.serveNumber}</strong>
                    <span>{formatArchiveTimestamp(serve.recordedAt)}</span>
                  </div>
                  <span className="session-tag">Ready</span>
                  <Link className="button button-primary small-button" to={`/feedback?video_id=${encodeURIComponent(serve.videoId)}`}>Open analysis</Link>
                </article>
              ))}
            </section>
            {nextCursor ? (
              <button className="button secondary-link archive-load-more" type="button" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? "Loading serves…" : "Load 50 more serves"}
              </button>
            ) : null}
          </>
        )}
      </div>
    </main>
  );
}

const COACH_NAMES = [
  "Technical Coach",
  "Motivational Coach",
  "Sports Scientist",
  "Junior Coach",
  "Fun/Humor Coach",
  "Big 3 Coach",
  "New Gen Coach",
];

function parseCoachFeedback(feedback) {
  if (!feedback) return [];

  const coachPattern = COACH_NAMES.join("|");
  const sectionPattern = new RegExp(
    `(?:^|\\n)\\s*(${coachPattern}):\\s*([\\s\\S]*?)(?=\\n\\s*(?:${coachPattern}):|$)`,
    "g"
  );
  const coaches = [];
  let match;

  while ((match = sectionPattern.exec(feedback)) !== null) {
    const content = match[2].trim();
    const drillMatch = content.match(/Drill:\\s*[“"]?([\\s\\S]*?)[”"]?\\s*$/i);
    coaches.push({
      name: match[1],
      guidance: content.replace(/\\s*Drill:\\s*[\\s\\S]*$/i, "").trim(),
      drill: drillMatch ? drillMatch[1].trim() : "",
    });
  }

  return coaches;
}

function getTakeaway(guidance) {
  const sentences = (guidance.match(/[^.!?]+[.!?]+/g) || [guidance])
    .map((sentence) => sentence.trim())
    .filter(Boolean);
  return sentences.slice(0, 2).join(" ");
}

function getFallbackDrill(coachingPoint) {
  const point = String(coachingPoint || "").toLowerCase();
  if (point.includes("knee")) return "Make three slow shadow serves. Pause at your deepest comfortable knee load, then drive up and forward without adding extra repetitions.";
  if (point.includes("toss")) return "Make five toss-only rehearsals from your serving stance. Let the ball rise on the same camera-view line before you serve again.";
  if (point.includes("shoulder") || point.includes("coil")) return "Make three slow shadow serves. Set your trophy position, then rehearse one smooth turn through the contact position.";
  if (point.includes("hip") || point.includes("landing")) return "Make three controlled shadow serves. Finish balanced inside the court, then record one full serve from the same camera setup.";
  if (point.includes("follow")) return "Make three relaxed shadow serves and hold your finish for two seconds after each one before recording the next full serve.";
  return "Make three slow shadow serves with this single focus, then record one full serve from the same camera setup.";
}

function formatMetricValue(value) {
  const normalized = String(value ?? "").trim();
  if (normalized === "1" || normalized.toUpperCase() === "TRUE") return "On track";
  if (normalized === "0" || normalized.toUpperCase() === "FALSE") return "Needs work";
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatMomentTime(timestampSec, frameId) {
  const seconds = Number.isFinite(timestampSec) ? timestampSec : frameId / 30;
  return `${seconds.toFixed(1)}s`;
}

function KeyMomentPreview({ moment, videoUrl, onSelect }) {
  const previewRef = React.useRef(null);
  const timestampSec = Number.isFinite(moment.timestampSec)
    ? moment.timestampSec
    : moment.frameId / 30;

  useEffect(() => {
    const preview = previewRef.current;
    if (!preview) return undefined;

    const seekToMoment = () => {
      preview.currentTime = timestampSec;
    };
    const holdFrame = () => {
      preview.pause();
    };

    preview.addEventListener("loadedmetadata", seekToMoment);
    preview.addEventListener("seeked", holdFrame);
    if (preview.readyState >= 1) seekToMoment();

    return () => {
      preview.removeEventListener("loadedmetadata", seekToMoment);
      preview.removeEventListener("seeked", holdFrame);
    };
  }, [timestampSec, videoUrl]);

  return (
    <button className="key-moment-card" type="button" onClick={() => onSelect(moment)}>
      <video ref={previewRef} src={videoUrl} muted playsInline preload="metadata" aria-hidden="true" />
      <span className="key-moment-copy">
        <strong>{moment.title}</strong>
        <small>{moment.detail}</small>
        <em>Watch slow motion · {formatMomentTime(moment.timestampSec, moment.frameId)}</em>
      </span>
    </button>
  );
}

function CoachingOverlay({ overlay, visible }) {
  if (!overlay) return null;

  const point = (joint) => overlay[joint] || null;
  const line = (from, to, className) => {
    const fromPoint = point(from);
    const toPoint = point(to);
    if (!fromPoint || !toPoint) return null;
    return (
      <line
        key={`${from}-${to}`}
        className={className}
        x1={fromPoint.x}
        y1={fromPoint.y}
        x2={toPoint.x}
        y2={toPoint.y}
      />
    );
  };
  const movementArrow = (from, to, className) => {
    const fromPoint = point(from);
    const toPoint = point(to);
    if (!fromPoint || !toPoint) return null;
    const length = Math.hypot(toPoint.x - fromPoint.x, toPoint.y - fromPoint.y);
    if (!length) return null;
    const unitX = (toPoint.x - fromPoint.x) / length;
    const unitY = (toPoint.y - fromPoint.y) / length;
    const wingLength = 0.025;
    const wingWidth = 0.014;
    const baseX = toPoint.x - unitX * wingLength;
    const baseY = toPoint.y - unitY * wingLength;
    const leftX = baseX - unitY * wingWidth;
    const leftY = baseY + unitX * wingWidth;
    const rightX = baseX + unitY * wingWidth;
    const rightY = baseY - unitX * wingWidth;
    return (
      <g key={`${from}-${to}`}>
        <line className={className} x1={fromPoint.x} y1={fromPoint.y} x2={toPoint.x} y2={toPoint.y} />
        <path className={className} d={`M ${leftX} ${leftY} L ${toPoint.x} ${toPoint.y} L ${rightX} ${rightY}`} />
      </g>
    );
  };
  const joint = (name) => {
    const jointPoint = point(name);
    return jointPoint ? <circle key={name} className="coaching-overlay-joint" cx={jointPoint.x} cy={jointPoint.y} r="0.014" /> : null;
  };
  const leftFoot = point("leftFoot");
  const rightFoot = point("rightFoot");
  const jumpOrigin = leftFoot && rightFoot
    ? {
      x: (leftFoot.x + rightFoot.x) / 2,
      y: Math.max(leftFoot.y, rightFoot.y),
    }
    : point("ankle");

  return (
    <svg
      className={`coaching-overlay ${visible ? "visible" : ""}`}
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {overlay.type === "knee" ? (
        <>
          {line("hip", "knee", "coaching-overlay-line knee")}
          {line("knee", "ankle", "coaching-overlay-line knee")}
          {joint("hip")}
          {joint("knee")}
          {joint("ankle")}
          {point("knee") ? (
            <text className="coaching-overlay-label" fontSize="0.055" x={point("knee").x + 0.025} y={point("knee").y - 0.025}>
              {Math.round(overlay.kneeAngleDeg)}°
            </text>
          ) : null}
        </>
      ) : null}
      {overlay.type === "alignment" ? (
        <>
          {line("tossWrist", "tossElbow", "coaching-overlay-line alignment")}
          {line("tossElbow", "tossShoulder", "coaching-overlay-line alignment")}
          {line("tossShoulder", "servingShoulder", "coaching-overlay-line alignment")}
          {line("servingShoulder", "servingElbow", "coaching-overlay-line alignment")}
          {line("tossWrist", "servingElbow", "coaching-overlay-guide")}
          {joint("tossWrist")}
          {joint("tossElbow")}
          {joint("tossShoulder")}
          {joint("servingShoulder")}
          {joint("servingElbow")}
          {Number.isFinite(overlay.alignmentDeviationPct) && point("tossWrist") ? (
            <text className="coaching-overlay-label" fontSize="0.045" x={point("tossWrist").x + 0.02} y={Math.max(0.05, point("tossWrist").y - 0.025)}>
              {overlay.alignmentDeviationPct}% path deviation
            </text>
          ) : null}
        </>
      ) : null}
      {overlay.type === "jump" && jumpOrigin ? (
        <>
          <line
            className="coaching-overlay-line jump"
            x1={jumpOrigin.x}
            y1={jumpOrigin.y}
            x2={jumpOrigin.x}
            y2={Math.max(0.05, jumpOrigin.y - 0.16)}
          />
          <path
            className="coaching-overlay-arrow"
            d={`M ${jumpOrigin.x - 0.025} ${jumpOrigin.y - 0.115} L ${jumpOrigin.x} ${jumpOrigin.y - 0.16} L ${jumpOrigin.x + 0.025} ${jumpOrigin.y - 0.115}`}
          />
          {joint("leftFoot")}
          {joint("rightFoot")}
          {Number.isFinite(overlay.jumpHeightPct) ? (
            <text className="coaching-overlay-label" fontSize="0.05" x={jumpOrigin.x + 0.025} y={Math.max(0.06, jumpOrigin.y - 0.175)}>
              {overlay.jumpHeightPct.toFixed(1)}% jump height
            </text>
          ) : null}
        </>
      ) : null}
      {overlay.type === "hip" ? (
        <>
          {movementArrow("previousHip", "hip", "coaching-overlay-line hip")}
          {point("previousHip") ? <circle className="coaching-overlay-start" cx={point("previousHip").x} cy={point("previousHip").y} r="0.014" /> : null}
          {joint("hip")}
          {Number.isFinite(overlay.hipTravelPct) && point("hip") ? (
            <text className="coaching-overlay-label" fontSize="0.045" x={point("hip").x + 0.02} y={point("hip").y - 0.025}>
              Hip travel: {overlay.hipTravelPct}% of visible torso length
            </text>
          ) : null}
        </>
      ) : null}
      {overlay.type === "uncoil" ? (
        <>
          {line("previousLeftShoulder", "previousRightShoulder", "coaching-overlay-guide")}
          {line("leftShoulder", "rightShoulder", "coaching-overlay-line uncoil")}
          {joint("leftShoulder")}
          {joint("rightShoulder")}
        </>
      ) : null}
      {overlay.type === "rotation" ? (
        <>
          {line("previousLeftShoulder", "previousRightShoulder", "coaching-overlay-line rotation-history")}
          {line("leftShoulder", "rightShoulder", "coaching-overlay-line rotation")}
          {joint("leftShoulder")}
          {joint("rightShoulder")}
        </>
      ) : null}
      {overlay.type === "contact-chain" ? (
        <>
          {line("contactWrist", "contactElbow", "coaching-overlay-line contact-chain")}
          {line("contactElbow", "contactShoulder", "coaching-overlay-line contact-chain")}
          {line("contactShoulder", "contactHip", "coaching-overlay-line contact-chain")}
          {line("contactHip", "contactKnee", "coaching-overlay-line contact-chain")}
          {line("contactKnee", "contactAnkle", "coaching-overlay-line contact-chain")}
          {line("contactWrist", "contactAnkle", "coaching-overlay-guide")}
          {joint("contactWrist")}
          {joint("contactElbow")}
          {joint("contactShoulder")}
          {joint("contactHip")}
          {joint("contactKnee")}
          {joint("contactAnkle")}
          {Number.isFinite(overlay.contactChainDeviationPct) && point("contactWrist") ? (
            <text className="coaching-overlay-label" fontSize="0.045" x={point("contactWrist").x + 0.02} y={Math.max(0.05, point("contactWrist").y - 0.025)}>
              {overlay.contactChainDeviationPct}% path deviation
            </text>
          ) : null}
        </>
      ) : null}
      {overlay.type === "contact-reach" ? (
        <>
          {line("shoulder", "elbow", "coaching-overlay-line contact-reach")}
          {line("elbow", "wrist", "coaching-overlay-line contact-reach")}
          {line("shoulder", "wrist", "coaching-overlay-guide")}
          {joint("shoulder")}
          {joint("elbow")}
          {joint("wrist")}
          {Number.isFinite(overlay.contactReachPct) && point("wrist") ? (
            <text className="coaching-overlay-label" fontSize="0.04" x={point("wrist").x + 0.02} y={Math.max(0.05, point("wrist").y - 0.05)}>
              Wrist reach: {overlay.contactReachPct}% above shoulder, relative to visible torso length
            </text>
          ) : null}
          {Number.isFinite(overlay.contactElbowAngleDeg) && point("elbow") ? (
            <text className="coaching-overlay-label" fontSize="0.04" x={point("elbow").x + 0.02} y={point("elbow").y + 0.04}>
              {overlay.contactElbowAngleDeg}° elbow
            </text>
          ) : null}
        </>
      ) : null}
    </svg>
  );
}

function KeyMomentFrame({ moment, videoUrl, onSelect }) {
  const frameRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const [captured, setCaptured] = useState(false);
  const timestampSec = Number.isFinite(moment.timestampSec)
    ? moment.timestampSec
    : moment.frameId / 30;

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    const seekToFrame = () => {
      frame.currentTime = timestampSec;
    };
    const holdFrame = () => {
      frame.pause();
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (!canvas || !context || !frame.videoWidth || !frame.videoHeight) {
        console.error("Unable to render key moment frame.");
        return;
      }

      canvas.width = frame.videoWidth;
      canvas.height = frame.videoHeight;
      context.drawImage(frame, 0, 0, canvas.width, canvas.height);
      setCaptured(true);
    };

    frame.addEventListener("loadedmetadata", seekToFrame);
    frame.addEventListener("seeked", holdFrame);
    if (frame.readyState >= 1) seekToFrame();

    return () => {
      frame.removeEventListener("loadedmetadata", seekToFrame);
      frame.removeEventListener("seeked", holdFrame);
    };
  }, [timestampSec, videoUrl]);

  return (
    <button className="key-moment-frame" type="button" onClick={() => onSelect(moment)}>
      <div className="key-moment-frame-stage">
        <video ref={frameRef} className="key-moment-frame-source" src={videoUrl} muted playsInline preload="metadata" aria-hidden="true" />
        <canvas ref={canvasRef} className={`key-moment-frame-canvas ${captured ? "captured" : ""}`} aria-label={`${moment.title} annotated frame`} role="img" />
        <CoachingOverlay overlay={moment.overlay} visible />
      </div>
      <div className="key-moment-frame-copy">
        <strong>{moment.title}</strong>
        <span>{moment.detail}</span>
        <small>Open annotated frame · {formatMomentTime(moment.timestampSec, moment.frameId)}</small>
      </div>
    </button>
  );
}

function KeyMomentFrameModal({ moment, videoUrl, onClose }) {
  const frameRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const timestampSec = Number.isFinite(moment.timestampSec)
    ? moment.timestampSec
    : moment.frameId / 30;

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    const seekToFrame = () => {
      frame.currentTime = timestampSec;
    };
    const captureFrame = () => {
      frame.pause();
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (!canvas || !context || !frame.videoWidth || !frame.videoHeight) {
        console.error("Unable to render key moment frame.");
        return;
      }

      canvas.width = frame.videoWidth;
      canvas.height = frame.videoHeight;
      context.drawImage(frame, 0, 0, canvas.width, canvas.height);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    frame.addEventListener("loadedmetadata", seekToFrame);
    frame.addEventListener("seeked", captureFrame);
    window.addEventListener("keydown", closeOnEscape);
    if (frame.readyState >= 1) seekToFrame();

    return () => {
      frame.removeEventListener("loadedmetadata", seekToFrame);
      frame.removeEventListener("seeked", captureFrame);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, timestampSec, videoUrl]);

  return (
    <div className="key-frame-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="key-frame-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="key-frame-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="slow-motion-close" type="button" onClick={onClose} aria-label="Close annotated frame">×</button>
        <p className="eyebrow">EXACT ANNOTATED FRAME</p>
        <h2 id="key-frame-title">{moment.title}</h2>
        <p>{moment.detail}</p>
        <div className="key-frame-modal-stage">
          <video ref={frameRef} className="key-moment-frame-source" src={videoUrl} muted playsInline preload="metadata" aria-hidden="true" />
          <canvas ref={canvasRef} className="key-moment-frame-canvas captured" role="img" aria-label={`${moment.title} annotated frame`} />
          <CoachingOverlay overlay={moment.overlay} visible />
        </div>
        <div className="key-frame-status">Frame {moment.frameId} · {formatMomentTime(moment.timestampSec, moment.frameId)}</div>
      </section>
    </div>
  );
}

const SKELETON_CONNECTIONS = [
  ["lm_11", "lm_12"], ["lm_11", "lm_13"], ["lm_13", "lm_15"],
  ["lm_12", "lm_14"], ["lm_14", "lm_16"], ["lm_11", "lm_23"],
  ["lm_12", "lm_24"], ["lm_23", "lm_24"], ["lm_23", "lm_25"],
  ["lm_25", "lm_27"], ["lm_24", "lm_26"], ["lm_26", "lm_28"],
];

function smoothPoseFrames(frames, preservedFrameId) {
  return frames.map((frame, index) => {
    if (frame.frameId === preservedFrameId) return frame;

    const surroundingFrames = frames.slice(Math.max(0, index - 1), index + 2);
    const joints = Object.fromEntries(Object.keys(frame.joints).map((jointName) => {
      const samples = surroundingFrames
        .map((candidate) => candidate.joints[jointName])
        .filter(Boolean);
      if (samples.length < 2) return [jointName, frame.joints[jointName]];

      return [jointName, {
        x: samples.reduce((total, joint) => total + joint.x, 0) / samples.length,
        y: samples.reduce((total, joint) => total + joint.y, 0) / samples.length,
        z: samples.reduce((total, joint) => total + joint.z, 0) / samples.length,
        confidence: samples.reduce((total, joint) => total + joint.confidence, 0) / samples.length,
      }];
    }));

    return { ...frame, joints };
  });
}

function SkeletonOverlay({ frame, trailFrames }) {
  if (!frame) return null;
  const jointRadius = (joint) => Math.max(0.007, Math.min(0.014, 0.01 - joint.z * 0.004));
  const jointOpacity = (joint) => Math.max(0.35, Math.min(1, joint.confidence));

  return (
    <svg className="skeleton-overlay" viewBox="0 0 1 1" preserveAspectRatio="none" aria-label="Animated pose skeleton" role="img">
      {["lm_15", "lm_16", "lm_27", "lm_28"].map((jointName) => {
        const points = trailFrames
          .map((trailFrame) => trailFrame.joints[jointName])
          .filter(Boolean);
        return points.slice(1).map((joint, index) => {
          const previous = points[index];
          return (
            <line
              key={`${jointName}-${index}`}
              className="skeleton-trail"
              x1={previous.x}
              y1={previous.y}
              x2={joint.x}
              y2={joint.y}
              style={{ opacity: (index + 1) / points.length }}
            />
          );
        });
      })}
      {SKELETON_CONNECTIONS.map(([from, to]) => {
        const start = frame.joints[from];
        const end = frame.joints[to];
        return start && end ? (
          <line
            key={`${from}-${to}`}
            x1={start.x}
            y1={start.y}
            x2={end.x}
            y2={end.y}
            style={{
              opacity: Math.min(jointOpacity(start), jointOpacity(end)),
              strokeWidth: 0.01 + Math.min(jointOpacity(start), jointOpacity(end)) * 0.01,
            }}
          />
        ) : null;
      })}
      {Object.entries(frame.joints).map(([name, joint]) => (
        <circle
          key={name}
          cx={joint.x}
          cy={joint.y}
          r={jointRadius(joint)}
          style={{ opacity: jointOpacity(joint) }}
        />
      ))}
    </svg>
  );
}

function SlowMotionLoop({ moment, videoUrl, videoId, onClose }) {
  const videoRef = React.useRef(null);
  const [isAtAnalyzedMoment, setIsAtAnalyzedMoment] = useState(false);
  const [motionView, setMotionView] = useState("coaching");
  const [poseFrames, setPoseFrames] = useState([]);
  const [poseLoading, setPoseLoading] = useState(true);
  const [poseError, setPoseError] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const timestampSec = Number.isFinite(moment.timestampSec)
    ? moment.timestampSec
    : moment.frameId / 30;
  const loopStartSec = Math.max(0, timestampSec - 0.6);
  const requestedLoopEndSec = timestampSec + 0.8;
  const smoothedPoseFrames = useMemo(
    () => smoothPoseFrames(poseFrames, moment.frameId),
    [moment.frameId, poseFrames]
  );
  const skeletonFrameIndex = useMemo(() => smoothedPoseFrames.reduce((closestIndex, frame, index) => (
    closestIndex === -1
      || Math.abs(frame.timestampSec - currentTime) < Math.abs(smoothedPoseFrames[closestIndex].timestampSec - currentTime)
      ? index
      : closestIndex
  ), -1), [currentTime, smoothedPoseFrames]);
  const skeletonFrame = skeletonFrameIndex >= 0 ? smoothedPoseFrames[skeletonFrameIndex] : null;
  const skeletonTrailFrames = skeletonFrameIndex >= 0
    ? smoothedPoseFrames.slice(Math.max(0, skeletonFrameIndex - 6), skeletonFrameIndex + 1)
    : [];
  const skeletonPhase = skeletonFrame?.phase || "Motion";

  useEffect(() => {
    let active = true;
    const startFrame = Math.max(1, moment.frameId - 22);
    const endFrame = moment.frameId + 27;

    async function loadPoseSequence() {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) throw new Error("Sign in is required to load skeleton motion.");
        const frames = await fetchServePoseSequence(token, videoId, startFrame, endFrame);
        if (active) {
          setPoseFrames(frames);
          setPoseError("");
        }
      } catch (error) {
        if (active) {
          setPoseError(error.message || "Skeleton motion is unavailable.");
        }
      } finally {
        if (active) setPoseLoading(false);
      }
    }

    loadPoseSequence();
    return () => {
      active = false;
    };
  }, [moment.frameId, videoId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    let loopEndSec = requestedLoopEndSec;
    const startLoop = () => {
      loopEndSec = Math.min(video.duration || requestedLoopEndSec, requestedLoopEndSec);
      video.currentTime = loopStartSec;
      video.playbackRate = 0.4;
      const playback = video.play();
      if (playback) {
        playback.catch((error) => {
          console.warn("Unable to start slow-motion replay:", error);
        });
      }
    };
    const keepLooping = () => {
      setIsAtAnalyzedMoment(Math.abs(video.currentTime - timestampSec) <= 0.18);
      setCurrentTime(video.currentTime);
      if (video.currentTime >= loopEndSec) {
        video.currentTime = loopStartSec;
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    video.addEventListener("loadedmetadata", startLoop);
    video.addEventListener("timeupdate", keepLooping);
    window.addEventListener("keydown", closeOnEscape);
    if (video.readyState >= 1) startLoop();

    return () => {
      video.pause();
      video.playbackRate = 1;
      video.removeEventListener("loadedmetadata", startLoop);
      video.removeEventListener("timeupdate", keepLooping);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [loopStartSec, onClose, requestedLoopEndSec, timestampSec, videoUrl]);

  return (
    <div className="slow-motion-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="slow-motion-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="slow-motion-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="slow-motion-close" type="button" onClick={onClose} aria-label="Close slow-motion replay">×</button>
        <p className="eyebrow">SLOW-MOTION REPLAY</p>
        <h2 id="slow-motion-title">{moment.title}</h2>
        <p>{moment.detail}. Watch the movement before and after this analyzed moment.</p>
        <div className="motion-view-switch" aria-label="Motion view">
          <button className={motionView === "coaching" ? "active" : ""} type="button" onClick={() => setMotionView("coaching")}>Coaching lines</button>
          <button className={motionView === "skeleton-video" ? "active" : ""} type="button" disabled={poseLoading || Boolean(poseError)} onClick={() => setMotionView("skeleton-video")}>Skeleton on video</button>
          <button className={motionView === "skeleton-only" ? "active" : ""} type="button" disabled={poseLoading || Boolean(poseError)} onClick={() => setMotionView("skeleton-only")}>Bones only</button>
        </div>
        {poseLoading ? <p className="motion-view-note">Loading skeleton motion…</p> : null}
        {poseError ? <p className="motion-view-error">{poseError}</p> : null}
        {motionView !== "coaching" ? (
          <div className="skeleton-storyboard" aria-label="Serve phase storyboard">
            {["Load", "Trophy", "Drive", "Contact", "Finish"].map((phase) => (
              <span key={phase} className={skeletonPhase.toLowerCase().includes(phase.toLowerCase()) ? "active" : ""}>{phase}</span>
            ))}
          </div>
        ) : null}
        <div className={`slow-motion-stage ${motionView === "skeleton-only" ? "bones-only" : ""}`}>
          <video ref={videoRef} className="slow-motion-video" src={videoUrl} muted playsInline preload="auto" />
          {motionView === "coaching" ? <CoachingOverlay overlay={moment.overlay} visible={isAtAnalyzedMoment} /> : null}
          {motionView !== "coaching" ? <SkeletonOverlay frame={skeletonFrame} trailFrames={skeletonTrailFrames} /> : null}
          {motionView !== "coaching" ? <span className="skeleton-phase">{skeletonPhase.replace(/_/g, " ")}</span> : null}
        </div>
        <div className="slow-motion-status"><span>↻</span> Looping at 0.4x speed</div>
      </section>
    </div>
  );
}

function isImprovementArea(metric) {
  return /(^0$|FALSE|NOT_|NEEDS|POOR|BAD)/.test(String(metric?.value ?? "").toUpperCase());
}

function formatSessionTitle(videoId, serveNumber) {
  const generatedMatch = String(videoId || "").match(
    /^match_(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})-(\d{2})-(\d{3})Z\.mp4$/
  );

  if (generatedMatch) {
    const [, year, month, day, hour, minute, second, millisecond] = generatedMatch;
    const recordedAt = new Date(
      `${year}-${month}-${day}T${hour}:${minute}:${second}.${millisecond}Z`
    );
    return {
      title: Number.isFinite(Number(serveNumber)) ? `Serve Rep #${serveNumber}` : "Serve Rep",
      detail: `Recorded ${recordedAt.toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })} at ${recordedAt.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      })}`,
    };
  }

  return {
    title: Number.isFinite(Number(serveNumber))
      ? `Serve Rep #${serveNumber}`
      : String(videoId || "Serve Rep")
      .replace(/\.[^.]+$/, "")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase()),
    detail: "Recorded serve",
  };
}

function FeedbackPage() {
  const [user, setUser] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loopMoment, setLoopMoment] = useState(null);
  const [frameMoment, setFrameMoment] = useState(null);
  const [searchParams] = useSearchParams();
  const videoId = searchParams.get("video_id");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user || !videoId) {
      setLoading(false);
      return;
    }

    let active = true;

    async function loadSummary() {
      try {
        const token = await user.getIdToken(true);
        const result = await fetchVideoSummary(token, videoId);
        if (active) {
          setSummary(result);
          setError("");
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message || "This analysis is not ready yet.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadSummary();
    return () => {
      active = false;
    };
  }, [user, videoId]);

  const metrics = summary?.summary || [];
  const coaches = parseCoachFeedback(summary?.feedback);
  const technicalCoach = coaches.find((coach) => coach.name === "Technical Coach") || coaches[0];
  const priorities = [
    ...metrics.filter(isImprovementArea),
    ...metrics.filter((metric) => !isImprovementArea(metric)),
  ].slice(0, 3);
  const session = formatSessionTitle(videoId);
  const keyMoments = summary?.key_moments || [];
  const visualRead = summary?.analysis_confidence;
  const primaryPriority = priorities[0];
  const primaryDrill = technicalCoach?.drill || getFallbackDrill(primaryPriority?.coaching_point);

  function beginNextRep() {
    localStorage.setItem("comparisonBaselineVideo", videoId);
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <Link className="wordmark" to="/">
          <span className="wordmark-icon">🎾</span>
          <span>Evolve <span className="wordmark-accent">Sports</span></span>
        </Link>
        <div className="account">
          <Link className="button button-quiet" to="/library">Back to library</Link>
        </div>
      </header>

      <div className="dashboard">
        {!videoId ? (
          <div className="page-shell">
            <p className="eyebrow">FEEDBACK</p>
            <h1>No video selected</h1>
            <p className="lead">Select a session from your library to open the analysis.</p>
            <Link className="button button-primary" to="/library">Go to library</Link>
          </div>
        ) : (
          <div className="detail-layout">
            <section className="detail-panel">
              <p className="eyebrow">AI FEEDBACK</p>
              <h1>{session.title}</h1>
              <p className="session-detail">{session.detail}</p>
              {loading ? (
                <div className="empty-state">Loading analysis…</div>
              ) : error ? (
                <div className="empty-state">{error}</div>
              ) : (
                <>
                  {summary?.feedback || priorities.length > 0 ? (
                    <section className="analysis-plan">
                      <div className="plan-heading">
                        <div>
                          <p className="eyebrow">YOUR NEXT SERVE PLAN</p>
                          <h2>One clear focus for your next rep.</h2>
                        </div>
                        <span className="plan-badge">Action plan</span>
                      </div>
                      {technicalCoach?.guidance ? (
                        <p className="plan-takeaway">{getTakeaway(technicalCoach.guidance)}</p>
                      ) : null}
                      {primaryPriority ? (
                        <div className="next-rep-card">
                          <div className="next-rep-heading">
                            <div>
                              <span>Primary focus</span>
                              <h3>{primaryPriority.coaching_point}</h3>
                            </div>
                            <small>{formatMetricValue(primaryPriority.value || "N/A")}</small>
                          </div>
                          <p>Train this one camera-visible cue on the next take. Keep the rest of your serve natural.</p>
                          <div className="next-rep-drill">
                            <span>Do this now</span>
                            <p>{primaryDrill}</p>
                          </div>
                          <Link className="button button-primary" to="/?next_rep=1" onClick={beginNextRep}>
                            Record your next rep
                          </Link>
                          <small className="next-rep-note">After it is analyzed, compare it with this serve using a similar camera setup.</small>
                        </div>
                      ) : null}
                      {priorities.length > 0 ? (
                       <div className="priority-section">
                         <span className="supporting-priorities-label">Supporting observations</span>
                         <div className="priority-grid">
                         {priorities.map((item, index) => (
                           <div key={`${item.coaching_point}-${index}`} className="priority-card">
                             <span>Priority {index + 1}</span>
                              <strong>{item.coaching_point}</strong>
                              <small>{formatMetricValue(item.value || "N/A")}</small>
                            </div>
                          ))}
                          </div>
                        </div>
                      ) : null}
                      {technicalCoach?.drill ? (
                        <div className="drill-card">
                          <span>Today&apos;s drill</span>
                          <p>{technicalCoach.drill}</p>
                        </div>
                      ) : null}
                    </section>
                  ) : null}
                  {summary?.url ? (
                    <video className="analysis-video" src={summary.url} controls preload="metadata" />
                  ) : null}
                  {summary?.url && keyMoments.length > 0 ? (
                    <section className="key-moments">
                      <div className="key-moments-heading">
                        <div>
                          <p className="eyebrow">KEY MOMENTS</p>
                          <h2>See your serve at the moments that matter.</h2>
                        </div>
                        <span>Tap a frame to review it in the replay</span>
                      </div>
                      <div className="key-moment-grid">
                        {keyMoments.map((moment) => (
                          <KeyMomentPreview
                            key={moment.id}
                            moment={moment}
                            videoUrl={summary.url}
                            onSelect={setLoopMoment}
                          />
                        ))}
                      </div>
                    </section>
                  ) : null}
                  {summary?.url && keyMoments.length > 0 ? (
                    <section className="key-moment-frames">
                      <div className="key-moment-frames-heading">
                        <div>
                          <p className="eyebrow">KEY MOMENT FRAMES</p>
                          <h2>Your exact positions, annotated.</h2>
                        </div>
                        <span>Lines and angles are shown at the analyzed frame.</span>
                      </div>
                      <div className="key-moment-frame-grid">
                        {keyMoments.map((moment) => (
                          <KeyMomentFrame
                            key={moment.id}
                            moment={moment}
                            videoUrl={summary.url}
                            onSelect={setFrameMoment}
                          />
                        ))}
                      </div>
                    </section>
                  ) : null}
                  {frameMoment ? (
                    <KeyMomentFrameModal
                      moment={frameMoment}
                      videoUrl={summary.url}
                      onClose={() => setFrameMoment(null)}
                    />
                  ) : null}
                  {loopMoment ? (
                    <SlowMotionLoop
                      moment={loopMoment}
                      videoUrl={summary.url}
                      videoId={videoId}
                      onClose={() => setLoopMoment(null)}
                    />
                  ) : null}
                  {metrics.length > 0 ? (
                    <details className="metrics-breakdown" open>
                      <summary>
                        <span>Full serve breakdown</span>
                        <small>{metrics.length} measured metrics</small>
                      </summary>
                      <div className="analysis-metric-grid">
                        {metrics.map((item, index) => (
                          <div key={`${item.coaching_point}-${index}`} className={`analysis-metric ${isImprovementArea(item) ? "watch" : "good"}`}>
                            <span>{item.coaching_point}</span>
                            <strong>{formatMetricValue(item.value || "N/A")}</strong>
                          </div>
                        ))}
                      </div>
                    </details>
                  ) : null}
                  {coaches.length > 1 ? (
                    <details className="coach-perspectives">
                      <summary>Explore the full coaching team <span>{coaches.length - 1} perspectives</span></summary>
                      <div className="coach-list">
                        {coaches.filter((coach) => coach !== technicalCoach).map((coach) => (
                          <article key={coach.name} className="coach-card">
                            <h3>{coach.name}</h3>
                            <p>{coach.guidance}</p>
                            {coach.drill ? <small><b>Drill:</b> {coach.drill}</small> : null}
                          </article>
                        ))}
                      </div>
                    </details>
                  ) : summary?.feedback ? (
                    <div className="analysis-card">
                      <h2>Coach notes</h2>
                      <p>{summary.feedback}</p>
                    </div>
                  ) : null}
                </>
              )}
            </section>

            <aside className="side-panel">
              {visualRead ? (
                <div className={`analysis-card visual-read ${visualRead.level}`}>
                  <p className="eyebrow">VISUAL READ</p>
                  <div className="visual-read-heading">
                    <h2>{visualRead.label}</h2>
                    {Number.isFinite(visualRead.value) ? <strong>{visualRead.value}%</strong> : null}
                  </div>
                  <p>{visualRead.note}</p>
                  <small>Camera-view movement patterns only.</small>
                </div>
              ) : null}
              <div className="analysis-card">
                <h2>What’s next</h2>
                <Link className="button button-primary button-wide" to={`/cx-chat?video_id=${encodeURIComponent(videoId)}`}>Ask about this serve</Link>
                <Link className="button button-primary button-wide" to="/?next_rep=1" onClick={beginNextRep}>Record your next rep</Link>
                <Link className="button button-primary button-wide" to={`/compare?video_id=${encodeURIComponent(videoId)}`}>Compare with another serve</Link>
                <Link className="button button-primary button-wide" to="/analytics">View progress analytics</Link>
                <Link className="button button-primary button-wide" to="/library">Review more sessions</Link>
              </div>
              <div className="analysis-card">
                <h2>Serve checklist</h2>
                <ul className="check-list">
                  <li>Watch the replay</li>
                  <li>Review the key metrics</li>
                  <li>Repeat the drill and compare the next take</li>
                </ul>
              </div>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}

function formatComparisonValue(metric) {
  const precision = metric.unit === "rad" ? 2 : 1;
  return `${metric.value.toFixed(precision)}${metric.unit ? ` ${metric.unit}` : ""}`;
}

function ServeFlowMap({ flow, ariaLabel }) {
  const [activeStop, setActiveStop] = useState(null);
  return (
    <div className="serve-flow-explorer">
      <div className="serve-flow-map" aria-label={ariaLabel}>
        <div className="serve-flow-line" aria-hidden="true" />
        {flow.map((stop) => (
          <button
            className={`flow-stop ${stop.tone}`}
            key={stop.label}
            type="button"
            onMouseEnter={() => setActiveStop(stop)}
            onFocus={() => setActiveStop(stop)}
            onClick={() => setActiveStop((current) => current?.label === stop.label ? null : stop)}
            aria-pressed={activeStop?.label === stop.label}
          >
            <i className="flow-stop-dot" aria-hidden="true" />
            <strong>{stop.label}</strong>
            <span>{stop.detail}</span>
          </button>
        ))}
      </div>
      {activeStop ? (
        <div className={`flow-evidence ${activeStop.tone}`} role="status">
          <strong>{activeStop.label} · {activeStop.detail}</strong>
          <span>{activeStop.evidence}</span>
          <small>Based on camera-view analysis.</small>
        </div>
      ) : null}
    </div>
  );
}

function MetricTrendChart({ title, subtitle, metricKey, unit, points, interpretation, getTone }) {
  const usable = points.filter((point) => Number.isFinite(point[metricKey]));
  const values = usable.map((point) => point[metricKey]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 1);
  const chartPoints = usable.map((point, index) => ({
    ...point,
    x: usable.length === 1 ? 50 : 8 + (index * 84) / (usable.length - 1),
    y: 82 - ((point[metricKey] - min) / range) * 64,
  }));
  const line = chartPoints.length < 2 ? "" : chartPoints.reduce((path, point, index) => {
    if (index === 0) return `M ${point.x} ${point.y}`;
    const previous = chartPoints[index - 1];
    const midpointX = (previous.x + point.x) / 2;
    return `${path} C ${midpointX} ${previous.y}, ${midpointX} ${point.y}, ${point.x} ${point.y}`;
  }, "");

  return (
    <article className="metric-trend-card">
      <h3>{title}</h3><p>{subtitle}</p>
      {chartPoints.length ? (
        <>
          <svg className="metric-line-chart" viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label={`${title} over recent serves`}>
            {line ? <path d={line} fill="none" stroke="#167b50" strokeWidth="2.5" vectorEffect="non-scaling-stroke" /> : null}
            {chartPoints.map((point, index) => (
              <Link key={point.videoId} to={`/feedback?video_id=${encodeURIComponent(point.videoId)}`}>
                <circle className={`metric-chart-point ${getTone(point[metricKey])}`} cx={point.x} cy={point.y} r="1.6"><title>Serve {index + 1}: {point[metricKey].toFixed(1)}{unit}</title></circle>
              </Link>
            ))}
          </svg>
          <div className="metric-trend-scale"><span>Low {min.toFixed(1)}{unit}</span><span>High {max.toFixed(1)}{unit}</span></div>
          <small>{interpretation}</small>
        </>
      ) : <div className="repeatability-empty"><span>◌</span><div><strong>More serves needed</strong><small>This metric will appear when it is measured in a completed serve.</small></div></div>}
    </article>
  );
}

function ServeProgressPage() {
  const [user, setUser] = useState(null);
  const [analytics, setAnalytics] = useState(defaultDashboardSummary);
  useEffect(() => onAuthStateChanged(auth, setUser), []);
  useEffect(() => {
    if (user) user.getIdToken().then(fetchDashboardSummary).then(setAnalytics);
  }, [user]);
  return <main className="app-shell"><header className="topbar"><Link className="wordmark" to="/"><span className="wordmark-icon">🎾</span><span>Evolve <span className="wordmark-accent">Sports</span></span></Link><Link className="button button-quiet" to="/analytics">Back to analytics</Link></header><div className="dashboard analytics-page"><p className="eyebrow">SERVE PROGRESS</p><h1>Track the measurements you train.</h1><p className="hero-copy">Your last {analytics.metricTrends.length} completed serves. Select a point to open that serve’s analysis.</p><section className="metric-trend-list">  <MetricTrendChart title="Deepest knee-load angle" subtitle="Green below 100° · amber 100–129° · red 130° and above" metricKey="kneeLoadAngle" unit="°" points={analytics.metricTrends} getTone={(value) => value < 100 ? "good" : value < 130 ? "watch" : "needs-work"} interpretation="Lower angle means more visible knee flexion; use similar camera setup for comparison." /><MetricTrendChart title="Jump height" subtitle="Green 8%+ · amber 4–7.9% · red below 4%" metricKey="jumpHeight" unit="%" points={analytics.metricTrends} getTone={(value) => value >= 8 ? "good" : value >= 4 ? "watch" : "needs-work"} interpretation="Relative camera-view lift bands, not physical jump height or a universal target." /><MetricTrendChart title="Follow-through arc" subtitle="Green above 120° · amber 90–120° · red below 90°" metricKey="followThroughArc" unit="°" points={analytics.metricTrends} getTone={(value) => value > 120 ? "good" : value >= 90 ? "watch" : "needs-work"} interpretation="Visible finish arc from the existing camera-view classification." /></section></div></main>;
}

function ComparisonPage() {
  const [user, setUser] = useState(null);
  const [videos, setVideos] = useState([]);
  const [currentSummary, setCurrentSummary] = useState(null);
  const [baselineSummary, setBaselineSummary] = useState(null);
  const [baselineVideoId, setBaselineVideoId] = useState("");
  const [selectedMomentId, setSelectedMomentId] = useState("serving-arm-alignment");
  const [frameSelection, setFrameSelection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const currentVideoId = searchParams.get("video_id");
  const requestedBaselineVideoId = searchParams.get("baseline_video_id");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user || !currentVideoId) {
      setLoading(false);
      return;
    }
    let active = true;
    async function loadComparisonSources() {
      try {
        const token = await user.getIdToken(true);
        const [summary, userVideos] = await Promise.all([
          fetchVideoSummary(token, currentVideoId),
          loadRecentUserVideos(user),
        ]);
        if (!active) return;
        const candidates = (Array.isArray(userVideos) ? userVideos : [])
          .map((video) => ({
            videoId: video?.name?.split("/").pop().replace(/^annotated_/, ""),
            serveNumber: video?.serveNumber,
          }))
          .filter((video) => video.videoId && video.videoId !== currentVideoId);
        setCurrentSummary(summary);
        setVideos(candidates);
        setBaselineVideoId((current) => {
          if (current) return current;
          if (requestedBaselineVideoId && candidates.some((video) => video.videoId === requestedBaselineVideoId)) {
            return requestedBaselineVideoId;
          }
          return candidates[0]?.videoId || "";
        });
        setError(candidates.length ? "" : "Record one more serve to unlock personal comparison.");
      } catch (loadError) {
        if (active) setError(loadError.message || "Could not load comparison data.");
      } finally {
        if (active) setLoading(false);
      }
    }
    loadComparisonSources();
    return () => {
      active = false;
    };
  }, [user, currentVideoId, requestedBaselineVideoId]);

  useEffect(() => {
    if (!user || !baselineVideoId) {
      setBaselineSummary(null);
      return;
    }
    let active = true;
    async function loadBaseline() {
      try {
        const result = await fetchVideoSummary(await user.getIdToken(true), baselineVideoId);
        if (active) setBaselineSummary(result);
      } catch (loadError) {
        if (active) setError(loadError.message || "Could not load the comparison serve.");
      }
    }
    loadBaseline();
    return () => {
      active = false;
    };
  }, [user, baselineVideoId]);

  const currentMetrics = new Map((currentSummary?.comparison_metrics || []).map((metric) => [metric.id, metric]));
  const baselineMetrics = new Map((baselineSummary?.comparison_metrics || []).map((metric) => [metric.id, metric]));
  const comparableMetrics = [...currentMetrics.values()].filter((metric) => baselineMetrics.has(metric.id));
  const currentMoments = currentSummary?.key_moments || [];
  const baselineMoments = baselineSummary?.key_moments || [];
  const comparableMoments = currentMoments.filter((moment) =>
    baselineMoments.some((baselineMoment) => baselineMoment.id === moment.id)
  );
  const activeMomentId = comparableMoments.some((moment) => moment.id === selectedMomentId)
    ? selectedMomentId
    : comparableMoments[0]?.id;
  const currentMoment = currentMoments.find((moment) => moment.id === activeMomentId);
  const baselineMoment = baselineMoments.find((moment) => moment.id === activeMomentId);
  const currentSession = formatSessionTitle(currentVideoId);
  const baselineServe = videos.find((video) => video.videoId === baselineVideoId);
  const baselineSession = formatSessionTitle(baselineVideoId, baselineServe?.serveNumber);

  if (!user) {
    return (
      <main className="app-shell app-page">
        <div className="page-shell auth-page">
          <p className="eyebrow">PERSONAL COMPARISON</p>
          <h1>Sign in to compare your serves</h1>
          <p className="lead">See matching moments and measurements from two sessions side by side.</p>
          <button className="button button-primary" onClick={signInWithGoogle}>Continue with Google</button>
        </div>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <Link className="wordmark" to="/"><span className="wordmark-icon">🎾</span><span>Evolve <span className="wordmark-accent">Sports</span></span></Link>
        <div className="account"><Link className="button button-quiet" to={currentVideoId ? `/feedback?video_id=${encodeURIComponent(currentVideoId)}` : "/library"}>Back to analysis</Link></div>
      </header>
      <div className="dashboard comparison-page">
        <section className="comparison-hero">
          <div>
            <p className="eyebrow">PERSONAL COMPARISON</p>
            <h1>See your next serve in context.</h1>
            <p className="hero-copy">Compare the same moments from two of your own sessions. Changes are video-based estimates, best reviewed with similar camera setup.</p>
          </div>
        </section>
        {loading ? <div className="empty-state">Preparing your comparison…</div> : error ? (
          <div className="empty-state"><div><p>{error}</p><Link className="button button-primary" to="/library">Back to library</Link></div></div>
        ) : (
          <>
            <section className="comparison-selector">
              <div><span>Earlier serve</span><strong>{baselineSession.title}</strong></div>
              <select value={baselineVideoId} onChange={(event) => setBaselineVideoId(event.target.value)} aria-label="Earlier serve">
                {videos.map((video) => {
                  const session = formatSessionTitle(video.videoId, video.serveNumber);
                  return <option value={video.videoId} key={video.videoId}>{session.title} · {session.detail}</option>;
                })}
              </select>
              <span className="comparison-arrow" aria-hidden="true">→</span>
              <div><span>Current serve</span><strong>{currentSession.title}</strong></div>
            </section>
            {!baselineSummary ? <div className="empty-state">Loading earlier serve…</div> : (
              <>
                <section className="comparison-card">
                  <div className="section-heading compact">
                    <div><p className="eyebrow">MEASURED CHANGE</p><h2>What changed between sessions</h2></div>
                    <span className="step-badge subtle">Same player · same camera angle recommended</span>
                  </div>
                  <p className="comparison-note">Camera-view estimates only. Use them to review your own repeatability—not force, speed, injury risk, or a universal ideal.</p>
                  <div className="comparison-metric-grid">
                    {comparableMetrics.map((metric) => {
                      const baselineMetric = baselineMetrics.get(metric.id);
                      const delta = metric.value - baselineMetric.value;
                      const sign = delta > 0 ? "+" : "";
                      return (
                        <article className="comparison-metric" key={metric.id}>
                          <span>{metric.label}</span>
                          <div><strong>{formatComparisonValue(baselineMetric)}</strong><i>→</i><strong>{formatComparisonValue(metric)}</strong></div>
                          <small>{sign}{delta.toFixed(metric.unit === "rad" ? 2 : 1)} {metric.unit} change</small>
                        </article>
                      );
                    })}
                  </div>
                </section>
                {currentMoment && baselineMoment ? (
                  <section className="comparison-card">
                    <div className="section-heading compact">
                      <div><p className="eyebrow">MATCHING KEY MOMENT</p><h2>See the positions side by side</h2></div>
                    </div>
                    <div className="comparison-moment-picker" role="group" aria-label="Choose a serve moment">
                      {comparableMoments.map((moment) => <button className={moment.id === activeMomentId ? "active" : ""} type="button" key={moment.id} onClick={() => setSelectedMomentId(moment.id)}>{moment.title}</button>)}
                    </div>
                    <div className="comparison-frames">
                      <div><p>Earlier · {formatMomentTime(baselineMoment.timestampSec, baselineMoment.frameId)}</p><KeyMomentFrame moment={baselineMoment} videoUrl={baselineSummary.url} onSelect={(moment) => setFrameSelection({ moment, videoUrl: baselineSummary.url })} /></div>
                      <div><p>Current · {formatMomentTime(currentMoment.timestampSec, currentMoment.frameId)}</p><KeyMomentFrame moment={currentMoment} videoUrl={currentSummary.url} onSelect={(moment) => setFrameSelection({ moment, videoUrl: currentSummary.url })} /></div>
                    </div>
                  </section>
                ) : null}
              </>
            )}
          </>
        )}
      </div>
      {frameSelection ? <KeyMomentFrameModal moment={frameSelection.moment} videoUrl={frameSelection.videoUrl} onClose={() => setFrameSelection(null)} /> : null}
    </main>
  );
}

function ProgressAnalyticsPage() {
  const [user, setUser] = useState(null);
  const [analytics, setAnalytics] = useState(defaultDashboardSummary);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let active = true;

    async function loadAnalytics() {
      try {
        const result = await fetchDashboardSummary(await user.getIdToken());
        if (active) setAnalytics(result);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadAnalytics();
    return () => {
      active = false;
    };
  }, [user]);

  const repeatability = analytics.repeatability;

  if (!user) {
    return (
      <main className="app-shell app-page">
        <div className="page-shell auth-page">
          <p className="eyebrow">PROGRESS ANALYTICS</p>
          <h1>Sign in to see your progress</h1>
          <p className="lead">Your serve trends and coaching priorities will appear here.</p>
          <button className="button button-primary" onClick={signInWithGoogle}>Continue with Google</button>
        </div>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <Link className="wordmark" to="/"><span className="wordmark-icon">🎾</span><span>Evolve <span className="wordmark-accent">Sports</span></span></Link>
        <div className="account"><Link className="button button-primary small-button" to="/progress">Serve progress</Link><Link className="button button-quiet" to="/library">Back to library</Link></div>
      </header>
      <div className="dashboard analytics-page">
        <section className="analytics-hero">
          <div>
            <p className="eyebrow">PROGRESS ANALYTICS</p>
            <h1>Make every session count.</h1>
            <p className="hero-copy">See what is improving, where to focus, and how your latest serve compares.</p>
          </div>
          <div className="analytics-score"><span>Completed serves</span><strong>{analytics.totalServes}</strong><small>available to review</small></div>
        </section>
        {loading ? <div className="empty-state">Loading your progress…</div> : (
          <>
            <section className="analytics-card repeatability-card">
              <div className="section-heading compact">
                <div>
                  <p className="eyebrow">REPEATABILITY</p>
                  <h2>How steady is your visible motion?</h2>
                </div>
                <span className="step-badge subtle">{repeatability.sessionCount} of {repeatability.minimumSessions} sessions</span>
              </div>
              {repeatability.isReady ? (
                <>
                  <p className="flow-intro">Variation across your latest completed serves. Lower spread means the camera-visible cue was more repeatable, not necessarily better.</p>
                  <div className="repeatability-grid">
                    {repeatability.metrics.map((metric) => (
                      <div className="repeatability-metric" key={metric.label}>
                        <span>{metric.label}</span>
                        <strong>{metric.value.toFixed(1)}{metric.unit ? ` ${metric.unit}` : ""}</strong>
                        <small>typical variation between serves</small>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="repeatability-empty">
                  <span>◌</span>
                  <div><strong>Build your baseline</strong><small>Record {Math.max(0, repeatability.minimumSessions - repeatability.sessionCount)} more similarly filmed serves to begin tracking repeatability.</small></div>
                </div>
              )}
            </section>
            <section className="analytics-card flow-card">
              <div className="section-heading compact">
                <div>
                  <p className="eyebrow">LATEST SERVE</p>
                  <h2>What happened on your latest serve</h2>
                </div>
                <span className="step-badge subtle">One serve</span>
              </div>
              <p className="flow-intro">A phase-by-phase view of the motion that produced your latest analysis.</p>
              <ServeFlowMap flow={analytics.latestServeFlow} ariaLabel="Latest serve flow" />
              <div className="flow-legend">
                <span><i className="flow-stop-dot good" /> Strength</span>
                <span><i className="flow-stop-dot watch" /> Mixed signal</span>
                <span><i className="flow-stop-dot needs-work" /> Needs attention</span>
                <span><i className="flow-stop-dot neutral" /> Measured, not graded</span>
              </div>
            </section>
            <section className="analytics-card flow-card">
              <div className="section-heading compact">
                <div>
                  <p className="eyebrow">ALL SERVES SO FAR</p>
                  <h2>Your recurring serve pattern</h2>
                </div>
                <span className="step-badge subtle">{analytics.totalServes} serves</span>
              </div>
              <p className="flow-intro">The same phase signals across every completed serve. Percentages show how often the signal was on track; use similarly filmed serves for the clearest pattern.</p>
              <ServeFlowMap flow={analytics.averageServeFlow} ariaLabel="Average serve flow across all serves" />
              <div className="flow-legend">
                <span><i className="flow-stop-dot good" /> Strength</span>
                <span><i className="flow-stop-dot watch" /> Mixed signal</span>
                <span><i className="flow-stop-dot needs-work" /> Needs attention</span>
                <span><i className="flow-stop-dot neutral" /> Measured, not graded</span>
              </div>
            </section>
            <section className="analytics-grid">
              <article className="analytics-card">
                <p className="eyebrow">SERVE MECHANICS AVERAGES</p>
                <h2>What your serve is telling you</h2>
                <p className="flow-intro">These are your averages across completed serves—not a grade. For knee load, a lower angle means a deeper visible bend; below 100° is shown as on track.</p>
                <div className="metric-grid">
                  {analytics.metrics.map((metric) => (
                    <div className={`metric-card ${metric.tone}`} key={metric.label}>
                      <span>{metric.label}</span><strong>{metric.value}{metric.unit}</strong><small>{metric.detail}</small>
                    </div>
                  ))}
                </div>
              </article>
              <article className="analytics-card">
                <p className="eyebrow">NEXT BEST WORK</p>
                <h2>Your practice priorities</h2>
                <ul className="focus-list">
                  {analytics.focusAreas.map((area) => (
                    <li className={area.tone} key={area.name}>
                      <div><span>{area.name}</span><strong>{area.score}%</strong></div>
                      <div className="mini-bar"><i style={{ width: `${area.score}%` }} /></div>
                    </li>
                  ))}
                </ul>
              </article>
            </section>
          </>
        )}
      </div>
    </main>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/tennis" element={<App />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/archive" element={<ServeArchivePage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/compare" element={<ComparisonPage />} />
        <Route path="/analytics" element={<ProgressAnalyticsPage />} />
        <Route path="/progress" element={<ServeProgressPage />} />
        <Route path="/cx-chat" element={<CxChatPage />} />
      </Routes>
    </Router>
  </React.StrictMode>
);
